import {
    AccidentClaimStatus,
    AccidentDocumentType,
    ClientAccident,
    ClientCompanyFile,
    ClientDOTInspection,
    ClientDrugTestRecord,
    ClientIFTARecord,
    ComplianceAlert,
    DOTInspectionParseResult,
    DOTInspectionStatus,
    DqfChecklistItemConfig,
    DqfDocumentFile,
    DqfSaveRole,
    DqfUploadMode,
    DqfUploadResponseData,
    SafetyDriver,
} from '@numeo/types-core'
import { SafetyLog, SafetyLogCategory, SafetyLogEntityType } from '@numeo/types-main-web-app'
import axios, { isAxiosError } from 'axios'
import { getApiErrorMessage } from '@/utils/get-user-error-message'
import { retryTransientRequest } from '@/utils/retry-transient-request'
import { getApiBaseUrlWithoutV1 } from '../setup/SetupAxios'
import { inspectionMultipartBody } from './inspectionAttachmentsApi'

// axios.defaults.baseURL already includes /v1, so this resolves to /v1/safety
const API_BASE_URL = '/safety'
// The newer DQF document endpoints follow the routing convention and are not under /v1 (see routes.ts dqfDocumentRouter).
const DQF_DOCUMENT_ROOT = '/safety'

// ─── Drivers ───

export type { SafetyDriver }

export const getSafetyDrivers = async (): Promise<SafetyDriver[]> => {
    const { data } = await axios.get(`${API_BASE_URL}/drivers`)
    return data?.data ?? data
}

type DriverDqfExpirationUpdates = Record<string, string | null>

export const updateDriverDqfExpirations = async (driverId: string, expirations: DriverDqfExpirationUpdates): Promise<void> => {
    await axios.put(`${API_BASE_URL}/drivers/dqf-documents/expiration`, { driverId, expirations })
}

interface UploadDqfDocumentOptions {
    mode?: DqfUploadMode
    /** Url of the extra file to swap in place (mode=replace only). */
    replaceFileUrl?: string
    /** One YYYY-MM-DD per file, in file order; required by the server for checklist items that track expiry. */
    expiries?: string[]
}

interface UploadDqfDocumentsResult {
    /** Files stored by the request (extras and replacements; the primary is listed in `primary` too). */
    saved: DqfDocumentFile[]
    /** Set when the primary file was created or replaced. */
    primary?: { url: string; name: string; expiry?: string }
    /** Set when one extra file was swapped in place. */
    replaced?: DqfDocumentFile
    /** Batch positions (order of `files` sent) of the files the server saved. */
    savedIndexes: number[]
    /** Files that did not upload or save, with the reason. */
    failed: { name: string; error?: string }[]
}

interface UploadDqfDocumentsResponse {
    data?: Partial<DqfUploadResponseData>
}

/** One file in one request; the server reports its outcome. */
const uploadDqfFile = async (driverId: string, docField: string, file: File, options?: UploadDqfDocumentOptions): Promise<UploadDqfDocumentsResult> => {
    const formData = new FormData()
    formData.append('files', file)
    formData.append('driverId', driverId)
    formData.append('docField', docField)
    options?.expiries?.forEach((expiry) => formData.append('expiries', expiry))
    if (options?.mode === DqfUploadMode.ADD) formData.append('mode', DqfUploadMode.ADD)
    if (options?.replaceFileUrl) {
        formData.append('mode', DqfUploadMode.REPLACE)
        formData.append('replaceFileUrl', options.replaceFileUrl)
    }
    const { data } = await axios.post<UploadDqfDocumentsResponse>(`${API_BASE_URL}/drivers/dqf-documents/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    })
    const uploadedAt = new Date().toISOString()
    const expiryAt = (index: number) => options?.expiries?.[index]
    const outcome: UploadDqfDocumentsResult = { saved: [], savedIndexes: [], failed: [] }
    for (const result of data.data?.results ?? []) {
        if (result.error || !result.url || !result.role) outcome.failed.push({ name: result.name, error: result.error })
        else {
            outcome.savedIndexes.push(result.index)
            if (result.role === DqfSaveRole.PRIMARY) outcome.primary = { url: result.url, name: result.name, expiry: expiryAt(result.index) }
            else if (result.role === DqfSaveRole.REPLACED) outcome.replaced = { url: result.url, name: result.name, uploadedAt, expiry: expiryAt(result.index) }
            else outcome.saved.push({ url: result.url, name: result.name, uploadedAt, expiry: expiryAt(result.index) })
        }
    }
    return outcome
}

/** How many times one file waits out a busy server before it is reported failed. */
const DQF_BUSY_RESENDS = 2
const DQF_DEFAULT_RETRY_AFTER_SECONDS = 5

/**
 * The wait a busy server asked for, or undefined for any other failure. The upload limiter answers 429/503 with `Retry-After` before it
 * reads the body, so nothing was saved and the same file can be sent again.
 */
const busyRetryAfterMs = (error: unknown): number | undefined => {
    if (!isAxiosError(error) || !error.response) return undefined
    const { status, headers } = error.response
    // axios lowercases response header names; main-service exposes Retry-After to the browser (CORS Expose-Headers).
    if ((status !== 429 && status !== 503) || headers?.['retry-after'] === undefined) return undefined
    const seconds = Number(headers['retry-after'])
    return (Number.isFinite(seconds) && seconds > 0 ? seconds : DQF_DEFAULT_RETRY_AFTER_SECONDS) * 1000
}

/**
 * Uploads the files one request per file, one after another, and reports each file's outcome by its position in `files`: the first file
 * in the caller's mode, the rest appended. One request at a time holds one of the project's upload slots, and a file the server is too
 * busy to take waits out its `Retry-After`. A later file that fails is marked failed, since the earlier ones are already saved.
 */
export const uploadDriverDqfDocuments = async (
    driverId: string,
    docField: string,
    files: File[],
    options?: UploadDqfDocumentOptions
): Promise<UploadDqfDocumentsResult> => {
    const outcome: UploadDqfDocumentsResult = { saved: [], savedIndexes: [], failed: [] }
    for (const [index, file] of files.entries()) {
        const fileOptions: UploadDqfDocumentOptions = {
            ...(index === 0 ? options : { mode: DqfUploadMode.ADD }),
            expiries: options?.expiries?.slice(index, index + 1),
        }
        let result: UploadDqfDocumentsResult
        try {
            result = await retryTransientRequest(() => uploadDqfFile(driverId, docField, file, fileOptions), {
                isTransient: (error) => busyRetryAfterMs(error) !== undefined,
                delayFor: (error, attempt) => (attempt < DQF_BUSY_RESENDS ? busyRetryAfterMs(error) : undefined),
            })
        } catch (error) {
            if (index === 0) throw error
            outcome.failed.push({ name: file.name, error: getApiErrorMessage(error, 'Upload failed') })
            continue
        }
        outcome.saved.push(...result.saved)
        if (result.savedIndexes.length > 0) outcome.savedIndexes.push(index)
        outcome.failed.push(...result.failed)
        outcome.primary ??= result.primary
        outcome.replaced ??= result.replaced
    }
    return outcome
}

/** Deletes one stored file of a DQF document type (the next file becomes the primary when the primary is deleted). */
export const deleteDriverDqfFile = async (driverId: string, docField: string, fileUrl: string): Promise<void> => {
    await axios.delete(`${DQF_DOCUMENT_ROOT}/drivers/dqf-documents/file/v1`, { baseURL: getApiBaseUrlWithoutV1(), params: { driverId, docField, fileUrl } })
}

/** Sets the expiration date (YYYY-MM-DD) of one stored file of a DQF document type. */
export const updateDriverDqfFileExpiration = async (driverId: string, docField: string, fileUrl: string, expirationDate: string): Promise<void> => {
    await axios.put(
        `${DQF_DOCUMENT_ROOT}/drivers/dqf-documents/file-expiration/v1`,
        { driverId, docField, fileUrl, expirationDate },
        { baseURL: getApiBaseUrlWithoutV1() }
    )
}

// ─── DQF Checklist Config ───

export interface AddDqfChecklistItemInput {
    label: string
    hasExpiry: boolean
}

export const getDqfChecklistConfig = async (): Promise<DqfChecklistItemConfig[]> => {
    const { data } = await axios.get(`${API_BASE_URL}/dqf-checklist-config`)
    return data?.data ?? data
}

export const addDqfChecklistItem = async (item: AddDqfChecklistItemInput): Promise<DqfChecklistItemConfig[]> => {
    const { data } = await axios.post(`${API_BASE_URL}/dqf-checklist-config`, item)
    return data?.data ?? data
}

export const updateDqfChecklistItem = async (
    key: string,
    updates: Partial<Pick<DqfChecklistItemConfig, 'label' | 'hasExpiry'>>
): Promise<DqfChecklistItemConfig[]> => {
    const { data } = await axios.put(`${API_BASE_URL}/dqf-checklist-config/${key}`, updates)
    return data?.data ?? data
}

export const removeDqfChecklistItem = async (key: string): Promise<DqfChecklistItemConfig[]> => {
    const { data } = await axios.delete(`${API_BASE_URL}/dqf-checklist-config/${key}`)
    return data?.data ?? data
}

// ─── Trucks ───

export interface SafetyTruck {
    id: string
    truckId: string
    mainInfo: string
    status: string
    drivers: string[]
    vin: string | null
    licensePlate: string | null
    state: string | null
}

export const getSafetyTrucks = async (): Promise<SafetyTruck[]> => {
    const { data } = await axios.get(`${API_BASE_URL}/trucks`)
    return data?.data ?? data
}

// ─── Dashboard ───

export interface SafetyDashboardData {
    driversNeedingAttention: number
    openDOTViolations: number
    openClaims: number
    activeDrivers: number
    complianceBreakdown: { compliant: number; expiring: number; expired: number }
    recentDOTInspections: ClientDOTInspection[]
    recentAccidents: ClientAccident[]
}

export const getSafetyDashboard = async (): Promise<SafetyDashboardData> => {
    const { data } = await axios.get(`${API_BASE_URL}/dashboard`)
    return data?.data ?? data
}

// ─── DOT Inspections ───

export interface DOTInspectionFilterParams {
    status?: DOTInspectionStatus
    onlyVoided?: boolean
    limit?: number
}

export const getDOTInspections = async (filters?: DOTInspectionFilterParams): Promise<ClientDOTInspection[]> => {
    const queryParams = new URLSearchParams()
    if (filters?.status) queryParams.append('status', filters.status)
    if (filters?.onlyVoided) queryParams.append('onlyVoided', 'true')
    if (filters?.limit) queryParams.append('limit', String(filters.limit))
    const { data } = await axios.get(`${API_BASE_URL}/dot-inspections?${queryParams.toString()}`)
    return data?.data ?? data
}

export const createDOTInspection = async (inspectionData: Partial<ClientDOTInspection>, files: File[] = []): Promise<ClientDOTInspection> => {
    const { data } = await axios.post(`${API_BASE_URL}/dot-inspections`, inspectionMultipartBody(inspectionData, files))
    return data?.data ?? data
}

// AI-parse an uploaded roadside DOT inspection report (PDF/JPEG/PNG) into review-ready fields.
/**
 * @deprecated The legacy immediate pipeline: the file goes to agents-service (`processPaperwork`, Gemini there) and the
 * request waits for it. Use ADR-022's immediate mode instead: `POST /documents/processing/immediate/v1` with
 * `docTypeHint: '<dot inspection type, once registered>'`, then `GET /documents/processing/jobs/:jobId/v1` on a 202 (the contract: ADR-022 Immediate mode).
 * There is no DOT inspection type in the new pipeline yet: existing callers move once one is registered and served (ADR-022.2 decision 9). Do not add new callers.
 */
export const parseDOTInspection = async (file: File): Promise<{ success: boolean; data: DOTInspectionParseResult }> => {
    const formData = new FormData()
    formData.append('file', file)
    const { data } = await axios.post(`${API_BASE_URL}/dot-inspections/parse`, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
    return data
}

export const getDOTInspectionById = async (id: string): Promise<ClientDOTInspection> => {
    const { data } = await axios.get(`${API_BASE_URL}/dot-inspections/${id}`)
    return data?.data ?? data
}

export const updateDOTInspection = async (id: string, inspectionData: Partial<ClientDOTInspection>): Promise<ClientDOTInspection> => {
    const { data } = await axios.put(`${API_BASE_URL}/dot-inspections/${id}`, inspectionData)
    return data?.data ?? data
}

export const voidDOTInspection = async (id: string, reason: string): Promise<ClientDOTInspection> => {
    const { data } = await axios.patch(`${API_BASE_URL}/dot-inspections/${id}/void`, { reason })
    return data?.data ?? data
}

export const restoreDOTInspection = async (id: string): Promise<ClientDOTInspection> => {
    const { data } = await axios.patch(`${API_BASE_URL}/dot-inspections/${id}/restore`)
    return data?.data ?? data
}

// ─── Accidents ───

export interface AccidentFilterParams {
    claimStatus?: AccidentClaimStatus
}

export const getAccidents = async (filters?: AccidentFilterParams): Promise<ClientAccident[]> => {
    const queryParams = new URLSearchParams()
    if (filters?.claimStatus) queryParams.append('claimStatus', filters.claimStatus)
    const { data } = await axios.get(`${API_BASE_URL}/accidents?${queryParams.toString()}`)
    return data?.data ?? data
}

export const createAccident = async (accidentData: Partial<ClientAccident>): Promise<ClientAccident> => {
    const { data } = await axios.post(`${API_BASE_URL}/accidents`, accidentData)
    return data?.data ?? data
}

export const getAccidentById = async (id: string): Promise<ClientAccident> => {
    const { data } = await axios.get(`${API_BASE_URL}/accidents/${id}`)
    return data?.data ?? data
}

export const updateAccident = async (id: string, accidentData: Partial<ClientAccident>): Promise<ClientAccident> => {
    const { data } = await axios.put(`${API_BASE_URL}/accidents/${id}`, accidentData)
    return data?.data ?? data
}

export const deleteAccident = async (id: string): Promise<void> => {
    await axios.delete(`${API_BASE_URL}/accidents/${id}`)
}

export const uploadAccidentDocument = async (
    accidentId: string,
    file: File,
    docType: AccidentDocumentType
): Promise<{ documentUrl: string; data: ClientAccident }> => {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('docType', docType)
    const { data } = await axios.post(`${API_BASE_URL}/accidents/${accidentId}/documents/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    })
    return data
}

// ─── Drug Tests ───

export const getDrugTests = async (): Promise<ClientDrugTestRecord[]> => {
    const { data } = await axios.get(`${API_BASE_URL}/drug-tests`)
    return data?.data ?? data
}

export const createDrugTest = async (testData: Partial<ClientDrugTestRecord>): Promise<ClientDrugTestRecord> => {
    const { data } = await axios.post(`${API_BASE_URL}/drug-tests`, testData)
    return data?.data ?? data
}

export const getDrugTestById = async (id: string): Promise<ClientDrugTestRecord> => {
    const { data } = await axios.get(`${API_BASE_URL}/drug-tests/${id}`)
    return data?.data ?? data
}

export const updateDrugTest = async (id: string, testData: Partial<ClientDrugTestRecord>): Promise<ClientDrugTestRecord> => {
    const { data } = await axios.put(`${API_BASE_URL}/drug-tests/${id}`, testData)
    return data?.data ?? data
}

// ─── IFTA Reports ───

interface IFTAReportsFilterParams {
    year?: number
    quarter?: string
    status?: string
    source?: string
    mcNumber?: string
    page?: number
    limit?: number
}

export const getIFTAReports = async (filters?: IFTAReportsFilterParams): Promise<ClientIFTARecord[]> => {
    const queryParams = new URLSearchParams()
    if (filters?.year !== undefined) queryParams.append('year', String(filters.year))
    if (filters?.quarter) queryParams.append('quarter', filters.quarter)
    if (filters?.status) queryParams.append('status', filters.status)
    if (filters?.source) queryParams.append('source', filters.source)
    if (filters?.mcNumber) queryParams.append('mcNumber', filters.mcNumber)
    if (filters?.page !== undefined) queryParams.append('page', String(filters.page))
    if (filters?.limit !== undefined) queryParams.append('limit', String(filters.limit))

    const suffix = queryParams.toString()
    const url = suffix ? `${API_BASE_URL}/ifta?${suffix}` : `${API_BASE_URL}/ifta`
    const { data } = await axios.get(url)
    return data?.data ?? data
}

export const createIFTAReport = async (reportData: Partial<ClientIFTARecord>): Promise<ClientIFTARecord> => {
    const { data } = await axios.post(`${API_BASE_URL}/ifta`, reportData)
    return data?.data ?? data
}

export const getIFTAReportById = async (id: string): Promise<ClientIFTARecord> => {
    const { data } = await axios.get(`${API_BASE_URL}/ifta/${id}`)
    return data?.data ?? data
}

export const updateIFTAReport = async (id: string, reportData: Partial<ClientIFTARecord>): Promise<ClientIFTARecord> => {
    const { data } = await axios.put(`${API_BASE_URL}/ifta/${id}`, reportData)
    return data?.data ?? data
}

export const generateIFTAReport = async (id: string): Promise<ClientIFTARecord> => {
    const { data } = await axios.post(`${API_BASE_URL}/ifta/${id}/generate`)
    return data?.data ?? data
}

export const deleteIFTAReport = async (id: string): Promise<void> => {
    await axios.delete(`${API_BASE_URL}/ifta/${id}`)
}

// ─── Company Files ───

export const uploadCompanyDocument = async (file: File): Promise<{ success: boolean; documentUrl: string }> => {
    const formData = new FormData()
    formData.append('file', file)
    const { data } = await axios.post(`${API_BASE_URL}/company-files/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
    })
    return data
}

export const getCompanyFiles = async (): Promise<ClientCompanyFile[]> => {
    const { data } = await axios.get(`${API_BASE_URL}/company-files`)
    return data?.data ?? data
}

export const createCompanyFile = async (fileData: Partial<ClientCompanyFile>): Promise<ClientCompanyFile> => {
    const { data } = await axios.post(`${API_BASE_URL}/company-files`, fileData)
    return data?.data ?? data
}

export const getCompanyFileById = async (id: string): Promise<ClientCompanyFile> => {
    const { data } = await axios.get(`${API_BASE_URL}/company-files/${id}`)
    return data?.data ?? data
}

export const updateCompanyFile = async (id: string, fileData: Partial<ClientCompanyFile>): Promise<ClientCompanyFile> => {
    const { data } = await axios.put(`${API_BASE_URL}/company-files/${id}`, fileData)
    return data?.data ?? data
}

export const deleteCompanyFile = async (id: string): Promise<void> => {
    await axios.delete(`${API_BASE_URL}/company-files/${id}`)
}

// ─── Compliance Alerts ───

export const getSafetyComplianceAlerts = async (): Promise<ComplianceAlert[]> => {
    const { data } = await axios.get(`${API_BASE_URL}/compliance/alerts`)
    return data?.data ?? []
}

// ─── Error Helper ───

/** Shared with the rest of the admin — see `@/utils/get-user-error-message`. */
export { getApiErrorMessage }

// ─── Activity logs ───

interface SafetyLogParams {
    category?: SafetyLogCategory
    limit?: number
    /** The `createdAt` (ISO) and `id` of the last entry shown: the page holds the entries just older than it. */
    beforeAt?: string
    beforeId?: string
}

/** One Safety record's activity log, newest first. New routing convention, so it sits outside /v1. */
export const getSafetyLogs = async (entityType: SafetyLogEntityType, entityId: string, params?: SafetyLogParams): Promise<SafetyLog[]> => {
    const { data } = await axios.get<{ data: SafetyLog[] }>(`/safety/activity-logs/${entityType}/${encodeURIComponent(entityId)}/v1`, {
        baseURL: getApiBaseUrlWithoutV1(),
        params,
    })
    return data.data || []
}
